import * as React from 'react';
import { ComponentProps} from 'ui/common/types';
import { Badge } from 'ui/badge';
import { BadgeList } from 'ui/badge-list';
import { ConnectedCasesRiskValueShape, ConnectedCasesIntegrityValueShape, ChildValueItemsShape,
         ConnectedCasesActionCountShape, ConnectedCasesBowtieSupportedShape} from 'app/area/case/selectors';
import { Icon } from 'ui/common/icon';
import { MdFormatListNumbered } from 'react-icons/md';
import styles from '../fact-box.scss';
import { toHex } from 'ui/common/hex-converter';
import {tableName, columnName} from 'app/area/case/case-data-model';
import { FactboxBadgeProps, FactboxBadgeListProps, SectionLink, FactBoxHideRiskWeight } from 'app/area/case/components/fact-box/types';
import { Link } from 'react-router-dom';
import { sectionFor } from 'app/area/case/components/fact-box/section';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { FactIconList } from 'app/area/case/components/fact-box/renderers';

const ConnectedFact = ({classNames='',section, url, children, urlOpenNew}) => {
  const navigateToSection = (e:React.MouseEvent<HTMLElement>) => {
    if(e.ctrlKey || e.metaKey) {
      return;
    }
  };
    return <li className={`${styles.factItem} ${classNames} ${styles.factLink}`} onClick={navigateToSection}>
            <Link to={`${url}?section=${section}`} className={styles.factLink} target={urlOpenNew && '_blank'}>
              {children}
            </Link>
          </li>;
};

const ConnectedBadge = (props: FactboxBadgeProps) => {
  const section = props.section;

  return(
    <ConnectedFact section={section} url={props.url} urlOpenNew={props.urlOpenNew}>
      <Badge
        tooltip={props.tooltip}
        backgroundColor={props.backgroundColor}
        foregroundColor={props.foregroundColor}
        filled={props.filled}
        text={props.value}
        title={props.title}
        riskTrend={props.riskTrend}
      />
    </ConnectedFact>);
};

const ConnectedBadgeList = (props: FactboxBadgeListProps) => {
  const { classNames, section } = props;

  return(
    <ConnectedFact classNames={classNames} url={props.url} section={section} urlOpenNew={props.urlOpenNew}>
      <BadgeList
        icon={props.icon}
        valueItems={props.valueItems}
        title={props.title}
      />
    </ConnectedFact>);
};

const ConnectedBadgeListIntegrityStatus = props => {
  const {connectedIntegrityStatus, connectedIntegrityScore, titleIntegrityStatus} = props;

  const iconIntegrityStatus = connectedIntegrityStatus ? <Icon url={connectedIntegrityStatus.icon}/> : null;
  const score = connectedIntegrityScore && connectedIntegrityScore;
  const tooltipIntegrityStatus = connectedIntegrityStatus && connectedIntegrityStatus.description;
  const items = [{key: 1, value: score, tooltip: tooltipIntegrityStatus}];
  const badgeListPropsConnectedIntegrityStatus = {
    title: titleIntegrityStatus,
    icon: iconIntegrityStatus,
    valueItems: items,
    links: props.links,
    url: props.url,
    urlOpenNew: props.urlOpenNew
  };

  return <ConnectedBadgeList 
    {...badgeListPropsConnectedIntegrityStatus} 
    classNames="" 
    section={sectionFor(props, 'IntegrityStatus')} />;
};

const ConnectedBadgeListIntegrityStatusBarrierEval= props => {
  const { titleIntegrityStatusBarrierEval, integrityStatusBarrierEval } = props;
  const iconIntegrityStatus = integrityStatusBarrierEval && (
    <Icon url={integrityStatusBarrierEval.icon} />
  );
  const tooltipIntegrityStatusBarrrierEval =
    integrityStatusBarrierEval && integrityStatusBarrierEval.description;
  const items = [
    { key: 1, value: undefined, tooltip: tooltipIntegrityStatusBarrrierEval }
  ];

  const badgeListPropsConnectedIntegrityStatusBarrierEval = {
    title: titleIntegrityStatusBarrierEval,
    icon: iconIntegrityStatus,
    valueItems: items,
    links: props.links,
    url: props.url,
    urlOpenNew: props.urlOpenNew
  };

  return <ConnectedBadgeList
    {...badgeListPropsConnectedIntegrityStatusBarrierEval}
    classNames=""
    section={sectionFor(props, 'BarrierEval')} />;
};

const ConnectedBadgeListActions = props => {
  const {actionCount, closedActionCount, tooltipClosedActions, tooltipNumberOfActions, titleActions} = props;
  const items = [{key: 1, value: actionCount, tooltip: tooltipNumberOfActions},
                 {key: 2, value: closedActionCount , tooltip: tooltipClosedActions,
                  classNames: (columnName[tableName.action] as any).numberOfClosedActions}];
  const badgeListPropsConnectedActions = {
    icon: <MdFormatListNumbered/>,
    title: titleActions,
    valueItems: items,
    links: props.links,
    url: props.url,
    urlOpenNew: props.urlOpenNew
  };

  return <ConnectedBadgeList
    {...badgeListPropsConnectedActions}
    classNames="actions-badge"
    section={sectionFor(props, tableName.action)} />;
};

const IconBowtie = props => {
  const iconBowtieAbsolutePath = toAbsoluteUrl('i/bowtie.svg');
  return <FactIconList title={props.titleBowtie} iconLine={true} url={`/bowtie/${props.primaryKey?.value}`} 
    icons={[{description: props.tooltipBowtie, icon: iconBowtieAbsolutePath, value: 0, propName: 'bowtie'}] as any}/>;
};

export const ConnectedCasesFactbox: React.FC<ConnectedCasesFooterProps> = props => {

  const renderList = () => {
    const {
      connectedRiskArea, connectedRiskWeight, connectedRiskAreaRest, connectedRiskWeightRest, connectedIntegrityStatus,
      actionCount, titleRiskArea, titleRiskAreaRest, connectedIntegrityStatusBarrierEval, hideRiskWeight, 
      connectedRiskTrend, connectedRiskTrendRest, connectedBowtieSupported  } = props;

    const badgePropsConnectedRiskArea = connectedRiskArea &&  {
      title: titleRiskArea,
      tooltip: connectedRiskArea.description,
      backgroundColor: connectedRiskArea.backgroundColor,
      foregroundColor: connectedRiskArea.foregroundColor,
      value: !hideRiskWeight ? connectedRiskWeight : '',
      links: props.links,
      url: props.url,
      urlOpenNew: props.urlOpenNew,
      riskTrend: { value: connectedRiskTrend?.[0]?.value, 
                  description: connectedRiskTrend?.[0]?.description, 
                  icon: connectedRiskTrend?.[0]?.icon }
    };

    const badgePropsConnectedRiskAreaRest = connectedRiskAreaRest && {
      title: titleRiskAreaRest,
      tooltip: connectedRiskAreaRest.description,
      filled: false,
      backgroundColor: connectedRiskAreaRest.backgroundColor,
      value: !hideRiskWeight ? connectedRiskWeightRest : '',
      links: props.links,
      url: props.url,
      urlOpenNew: props.urlOpenNew,
      riskTrend: { value: connectedRiskTrendRest?.[0]?.value, 
                  description: connectedRiskTrendRest?.[0]?.description, 
                  icon: connectedRiskTrendRest?.[0]?.icon }
    };
    const bgColor = connectedRiskArea && connectedRiskArea.value && connectedRiskArea.backgroundColor;
    const items = [
      connectedRiskArea && !!connectedRiskArea.value && 
        <ConnectedBadge 
          key="1"
          {...badgePropsConnectedRiskArea} 
          section={sectionFor(props, tableName.lossPotentialEvaluation)} />,

      connectedRiskAreaRest && !!connectedRiskAreaRest.value && 
        <ConnectedBadge 
          key="2"
          {...badgePropsConnectedRiskAreaRest} 
          section={sectionFor(props, tableName.lossPotentialResidualEvaluation)} />,

      connectedIntegrityStatus && !!connectedIntegrityStatus.value && 
        <ConnectedBadgeListIntegrityStatus 
          key="3"
          {...props} />,
      
      !!actionCount && 
        <ConnectedBadgeListActions 
          key="4"
          {...props} />,

      connectedIntegrityStatusBarrierEval && !!connectedIntegrityStatusBarrierEval.value && 
        <ConnectedBadgeListIntegrityStatusBarrierEval 
          key="5"
          {...props} />,

      connectedBowtieSupported && <IconBowtie key="6" {...props} />
    ].filter(item => item);
    return items.length !== 0 ? (
      <ul className={styles.factBoxList} style={{borderColor: toHex(bgColor)}}>
        {items}
      </ul>
    ) : null;
  };

    const list = renderList();
    return list ? (
      <div id={props.componentId} className={`${props.className} ${styles.factBox}`}>
        {list}
      </div>
    ) : null;
};
export interface ConnectedCasesFooterTranslations extends ComponentProps {
  tooltipNumberOfActions: string;
  tooltipClosedActions: string;
  titleRiskArea?: string;
  titleRiskAreaRest?: string;
  titleIntegrityStatus?: string;
  titleActions?: string;
}

export type ConnectedCasesFooterProps =
ConnectedCasesRiskValueShape &
ConnectedCasesIntegrityValueShape &
ConnectedCasesActionCountShape &
ChildValueItemsShape &
ConnectedCasesFooterTranslations &
ComponentProps &
FactBoxHideRiskWeight &
SectionLink &
ConnectedCasesBowtieSupportedShape;
